import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {Map<string, Element>} */
  #cachedContent = new Map();

  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard =
      /** @type {import('./product-card').ProductCard | null} */ (
        this.closest('product-card')
      );

    if (productCard) return productCard.productPageUrl;

    const hotspotProduct =
      /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
        this.closest('product-hotspot-component')
      );

    const productLink = hotspotProduct?.getHotspotProductLink();

    return productLink?.href || '';
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null}
   */
  #getSelectedVariantId() {
    const productCard =
      /** @type {import('./product-card').ProductCard | null} */ (
        this.closest('product-card')
      );

    return productCard?.getSelectedVariantId() ?? null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener(
      'change',
      this.#closeQuickAddModal
    );

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartUpdate,
      {
        signal: this.#cartUpdateAbortController.signal,
      }
    );

    document.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelectUpdate
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener(
      'change',
      this.#closeQuickAddModal
    );

    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();

    document.removeEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelectUpdate
    );
  }

  /**
   * Updates quick-add button state when product variant is selected
   * @param {ProductSelectEvent} event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;

    if (
      event.target.closest('product-card') !==
      this.closest('product-card')
    ) {
      return;
    }

    if (this.dataset.usesSellingPlans === 'true') return;

    if (this.dataset.rendersBothButtons !== 'true') return;

    const productOptionsCount =
      this.dataset.productOptionsCount;

    const quickAddButton =
      productOptionsCount === '1'
        ? 'add'
        : 'choose';

    this.setAttribute(
      'data-quick-add-button',
      quickAddButton
    );
  };

  /**
   * Clears cached Quick Add product content when cart changes.
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders variant picker.
   * @param {Element} newHtml
   */
  #updateVariantPicker(newHtml) {
    const modalContent =
      document.getElementById(
        'quick-add-modal-content'
      );

    if (!modalContent) return;

    const variantPicker =
      /** @type {VariantPicker | null} */ (
        modalContent.querySelector(
          'variant-picker'
        )
      );

    if (!variantPicker) return;

    variantPicker.updateVariantPicker(
      newHtml
    );
  }

  /**
   * Handles Quick Add button click.
   * @param {Event} event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl =
      this.productPageUrl;

    if (
      this.dataset.usesSellingPlans ===
      'true'
    ) {
      if (currentUrl) {
        window.location.href =
          currentUrl;
      }

      return;
    }

    let productGrid =
      this.#cachedContent.get(
        currentUrl
      );

    if (!productGrid) {
      const html =
        await this.fetchProductPage(
          currentUrl
        );

      if (html) {
        const gridElement =
          html.querySelector(
            '[data-product-grid-content]'
          );

        if (gridElement) {
          productGrid =
            /** @type {Element} */ (
              gridElement.cloneNode(true)
            );

          this.#cachedContent.set(
            currentUrl,
            productGrid
          );
        }
      }
    }

    if (productGrid) {
      const freshContent =
        /** @type {Element} */ (
          productGrid.cloneNode(true)
        );

      await this.updateQuickAddModal(
        freshContent
      );

      this.#updateVariantPicker(
        productGrid
      );
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent =
      document.getElementById(
        'quick-add-dialog'
      );

    if (
      !(
        dialogComponent instanceof
        QuickAddDialog
      )
    ) {
      return;
    }

    const productDetails =
      dialogComponent.querySelector(
        '.product-details'
      );

    const productMedia =
      dialogComponent.querySelector(
        '.product-information__media'
      );

    productDetails?.scrollTo({
      top: 0,
      behavior: 'instant',
    });

    productMedia?.scrollTo({
      top: 0,
      behavior: 'instant',
    });
  }

  /**
   * @param {QuickAddDialog} dialogComponent
   */
  #stayVisibleUntilDialogCloses(
    dialogComponent
  ) {
    this.toggleAttribute(
      'stay-visible',
      true
    );

    dialogComponent.addEventListener(
      DialogCloseEvent.eventName,
      () =>
        this.toggleAttribute(
          'stay-visible',
          false
        ),
      {
        once: true,
      }
    );
  }

  #openQuickAddModal = () => {
    const dialogComponent =
      document.getElementById(
        'quick-add-dialog'
      );

    if (
      !(
        dialogComponent instanceof
        QuickAddDialog
      )
    ) {
      return;
    }

    this.#stayVisibleUntilDialogCloses(
      dialogComponent
    );

    dialogComponent.showDialog();

    const dialog =
      dialogComponent.refs?.dialog;

    if (!dialog) return;

    dialog.addEventListener(
      'animationstart',
      this.#resetScroll.bind(this),
      {
        once: true,
      }
    );
  };

  #closeQuickAddModal = () => {
    const dialogComponent =
      document.getElementById(
        'quick-add-dialog'
      );

    if (
      !(
        dialogComponent instanceof
        QuickAddDialog
      )
    ) {
      return;
    }

    dialogComponent.closeDialog();
  };

  /**
   * Fetch product page.
   * @param {string} productPageUrl
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(
    productPageUrl
  ) {
    if (!productPageUrl) return null;

    this.#abortController?.abort();

    this.#abortController =
      new AbortController();

    try {
      const response = await fetch(
        productPageUrl,
        {
          signal:
            this.#abortController
              .signal,
        }
      );

      if (!response.ok) {
        throw new Error(
          `Failed to fetch product page: HTTP error ${response.status}`
        );
      }

      const responseText =
        await response.text();

      return new DOMParser().parseFromString(
        responseText,
        'text/html'
      );
    } catch (error) {
      if (
        error.name === 'AbortError'
      ) {
        return null;
      }

      throw error;
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Builds Quick Add modal.
   * @param {Element} productGrid
   */
  async updateQuickAddModal(
    productGrid
  ) {
    const modalContent =
      document.getElementById(
        'quick-add-modal-content'
      );

    if (
      !productGrid ||
      !modalContent
    ) {
      return;
    }

    /*
     * QUICK ADD INVENTORY FIX
     *
     * Your working Buy Buttons block already
     * outputs:
     *
     * data-niera-variant-quantity-data
     *
     * Mobile Quick Add restructures the
     * product content and removes
     * .product-details.
     *
     * Preserve the inventory JSON before
     * that happens.
     */
    const inventoryDataNode =
      productGrid.querySelector(
        '[data-niera-variant-quantity-data]'
      );

    const inventoryDataClone =
      inventoryDataNode?.cloneNode(true) ||
      null;

    if (isMobileBreakpoint()) {
      const productDetails =
        productGrid.querySelector(
          '.product-details'
        );

      const productFormComponent =
        productGrid.querySelector(
          'product-form-component'
        );

      const variantPicker =
        productGrid.querySelector(
          'variant-picker'
        );

      const productPrice =
        productGrid.querySelector(
          'product-price'
        );

      const productTitle =
        document.createElement('a');

      productTitle.textContent =
        this.dataset.productTitle || '';

      productTitle.href =
        this.productPageUrl;

      const productHeader =
        document.createElement('div');

      productHeader.classList.add(
        'product-header'
      );

      productHeader.appendChild(
        productTitle
      );

      if (productPrice) {
        productHeader.appendChild(
          productPrice
        );
      }

      productGrid.appendChild(
        productHeader
      );

      if (variantPicker) {
        productGrid.appendChild(
          variantPicker
        );
      }

      if (productFormComponent) {
        productGrid.appendChild(
          productFormComponent
        );
      }

      productDetails?.remove();
    }

    /*
     * Restore inventory data if mobile
     * restructuring removed it.
     */
    if (
      inventoryDataClone &&
      !productGrid.querySelector(
        '[data-niera-variant-quantity-data]'
      )
    ) {
      productGrid.appendChild(
        inventoryDataClone
      );
    }

    const payload =
      productGrid.getAttribute(
        'view-event-payload'
      ) || '';

    modalContent.setAttribute(
      'view-event-payload',
      payload
    );

    morph(
      modalContent,
      productGrid
    );

    this.#syncVariantSelection(
      modalContent
    );

    /*
     * Apply inventory maximum after the
     * Quick Add DOM is ready.
     */
    const dialogComponent =
      document.getElementById(
        'quick-add-dialog'
      );

    if (
      dialogComponent instanceof
      QuickAddDialog
    ) {
      requestAnimationFrame(() => {
        dialogComponent.syncInventoryQuantity();
      });
    }
  }

  /**
   * Sync product-card variant to modal.
   * @param {Element} modalContent
   */
  #syncVariantSelection(
    modalContent
  ) {
    const selectedVariantId =
      this.#getSelectedVariantId();

    if (!selectedVariantId) return;

    const modalInputs =
      modalContent.querySelectorAll(
        'input[type="radio"][data-variant-id]'
      );

    for (const input of modalInputs) {
      if (
        input instanceof
          HTMLInputElement &&
        input.dataset.variantId ===
          selectedVariantId &&
        !input.checked
      ) {
        input.checked = true;

        input.dispatchEvent(
          new Event('change', {
            bubbles: true,
          })
        );

        break;
      }
    }
  }
}

if (
  !customElements.get(
    'quick-add-component'
  )
) {
  customElements.define(
    'quick-add-component',
    QuickAddComponent
  );
}

class QuickAddDialog extends DialogComponent {
  #abortController =
    new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.handleCartUpdate,
      {
        signal:
          this.#abortController
            .signal,
      }
    );

    this.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect
    );

    this.addEventListener(
      DialogCloseEvent.eventName,
      this.#handleDialogClose
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();

    this.removeEventListener(
      DialogCloseEvent.eventName,
      this.#handleDialogClose
    );
  }

  /**
   * QUICK ADD ONLY
   *
   * Synchronize quantity selector with
   * actual selected variant inventory.
   *
   * @param {Document | Element | null} sourceHtml
   */
  syncInventoryQuantity(
    sourceHtml = null
  ) {
    const modalContent =
      this.querySelector(
        '#quick-add-modal-content'
      ) || this;

    const formComponent =
      modalContent.querySelector(
        'product-form-component'
      );

    if (!formComponent) return;

    /*
     * Get selected variant.
     *
     * When changing size/color,
     * sourceHtml contains the newly
     * rendered selected variant.
     */
    const modalVariantInput =
      formComponent.querySelector(
        'input[name="id"][ref="variantId"]'
      ) ||
      formComponent.querySelector(
        'input[name="id"]'
      );

    const sourceVariantInput =
      sourceHtml?.querySelector?.(
        'product-form-component input[name="id"][ref="variantId"]'
      ) ||
      sourceHtml?.querySelector?.(
        'product-form-component input[name="id"]'
      );

    const variantId =
      sourceVariantInput?.value ||
      modalVariantInput?.value;

    if (!variantId) return;

    /*
     * Read inventory information already
     * generated by your working
     * buy-buttons.liquid.
     */
    const dataNode =
      sourceHtml?.querySelector?.(
        '[data-niera-variant-quantity-data]'
      ) ||
      modalContent.querySelector(
        '[data-niera-variant-quantity-data]'
      );

    if (!dataNode) return;

    let variants;

    try {
      variants = JSON.parse(
        dataNode.textContent || '{}'
      );
    } catch (error) {
      console.warn(
        '[quick-add] Unable to parse inventory data.',
        error
      );

      return;
    }

    const info =
      variants[String(variantId)];

    if (!info) return;

    const min = Math.max(
      Number(info.min) || 1,
      1
    );

    const step = Math.max(
      Number(info.increment) || 1,
      1
    );

    const cartQuantity = Math.max(
      Number(info.cart_quantity) || 0,
      0
    );

    const ruleMax =
      info.rule_max === null ||
      info.rule_max === undefined ||
      info.rule_max === ''
        ? null
        : Number(info.rule_max);

    /*
     * Only limit by inventory when:
     *
     * Track quantity = ON
     * Continue selling out of stock = OFF
     */
    const inventoryIsLimited =
      info.inventory_management ===
        'shopify' &&
      info.inventory_policy ===
        'deny';

    let totalMax = ruleMax;

    if (inventoryIsLimited) {
      const inventoryQuantity =
        Math.max(
          Number(
            info.inventory_quantity
          ) || 0,
          0
        );

      totalMax =
        totalMax == null
          ? inventoryQuantity
          : Math.min(
              totalMax,
              inventoryQuantity
            );
    }

    /*
     * Locate Horizon native quantity
     * component inside Quick Add.
     */
    const quantitySelector =
      formComponent.querySelector(
        'quantity-selector-component'
      );

    const quantityInput =
      quantitySelector?.querySelector(
        '[ref="quantityInput"]'
      ) ||
      formComponent.querySelector(
        'input[name="quantity"]'
      ) ||
      formComponent.querySelector(
        '[ref="quantityInput"]'
      );

    if (
      !(
        quantityInput instanceof
        HTMLInputElement
      )
    ) {
      return;
    }

    /*
     * Horizon uses cart quantity when
     * calculating how many MORE items
     * can be added.
     */
    quantityInput.setAttribute(
      'data-cart-quantity',
      String(cartQuantity)
    );

    /*
     * Use Horizon's own quantity
     * component methods whenever
     * available.
     */
    if (
      quantitySelector &&
      typeof quantitySelector
        .setCartQuantity === 'function'
    ) {
      quantitySelector.setCartQuantity(
        cartQuantity
      );
    }

    if (
      quantitySelector &&
      typeof quantitySelector
        .updateConstraints === 'function'
    ) {
      quantitySelector.updateConstraints(
        String(min),
        totalMax == null
          ? null
          : String(totalMax),
        String(step)
      );

      return;
    }

    /*
     * Fallback for Horizon versions
     * without updateConstraints().
     */
    quantityInput.min =
      String(min);

    quantityInput.step =
      String(step);

    if (totalMax == null) {
      quantityInput.removeAttribute(
        'max'
      );
    } else {
      quantityInput.max =
        String(totalMax);
    }

    /*
     * Actual amount that can still be
     * added after considering cart.
     */
    const effectiveMax =
      totalMax == null
        ? null
        : Math.max(
            totalMax -
              cartQuantity,
            0
          );

    let current =
      Number(quantityInput.value) ||
      min;

    current = Math.max(
      current,
      min
    );

    if (effectiveMax != null) {
      current = Math.min(
        current,
        Math.max(
          effectiveMax,
          min
        )
      );
    }

    quantityInput.value =
      String(current);

    const minusButton =
      quantitySelector?.querySelector(
        '[ref="minusButton"]'
      ) ||
      formComponent.querySelector(
        '[name="minus"]'
      );

    const plusButton =
      quantitySelector?.querySelector(
        '[ref="plusButton"]'
      ) ||
      formComponent.querySelector(
        '[name="plus"]'
      );

    if (
      minusButton instanceof
      HTMLButtonElement
    ) {
      minusButton.disabled =
        current <= min;
    }

    if (
      plusButton instanceof
      HTMLButtonElement
    ) {
      plusButton.disabled =
        effectiveMax != null &&
        current >= effectiveMax;
    }
  }

  /**
   * Close Quick Add after successful
   * cart update.
   * @param {CartLinesUpdateEvent} event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;

        this.closeDialog();
      })
      .catch((error) => {
        if (
          error?.name !==
          'AbortError'
        ) {
          console.warn(
            '[quick-add] Event promise rejected:',
            error
          );
        }
      });
  };

  /**
   * Runs whenever Color / Size /
   * other variant option changes.
   *
   * @param {ProductSelectEvent} event
   */
  #handleProductSelect = (
    event
  ) => {
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;

        const anchorElement =
          /** @type {HTMLAnchorElement} */ (
            html.querySelector(
              '.view-product-title a'
            )
          );

        const viewMoreDetailsLink =
          /** @type {HTMLAnchorElement} */ (
            this.querySelector(
              '.view-product-title a'
            )
          );

        const mobileProductTitle =
          /** @type {HTMLAnchorElement} */ (
            this.querySelector(
              '.product-header a'
            )
          );

        /*
         * IMPORTANT:
         * Update inventory on EVERY
         * variant change.
         */
        requestAnimationFrame(() => {
          this.syncInventoryQuantity(
            html
          );
        });

        if (!anchorElement) return;

        if (viewMoreDetailsLink) {
          viewMoreDetailsLink.href =
            anchorElement.href;
        }

        if (mobileProductTitle) {
          mobileProductTitle.href =
            anchorElement.href;
        }
      })
      .catch((error) => {
        if (
          error?.name !==
          'AbortError'
        ) {
          console.warn(
            '[quick-add] Event promise rejected:',
            error
          );
        }
      });
  };

  #handleDialogClose = () => {
    const iosVersion =
      getIOSVersion();

    if (
      !iosVersion ||
      iosVersion.major >= 17 ||
      (
        iosVersion.major === 16 &&
        iosVersion.minor >= 4
      )
    ) {
      return;
    }

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid =
        document.querySelector(
          '#ResultsList [product-grid-view]'
        );

      if (grid) {
        const currentWidth =
          grid.getBoundingClientRect()
            .width;

        grid.style.width =
          `${currentWidth - 1}px`;

        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (
  !customElements.get(
    'quick-add-dialog'
  )
) {
  customElements.define(
    'quick-add-dialog',
    QuickAddDialog
  );
}